import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import CinematicCanvas from "@/components/landing/CinematicCanvas";

/**
 * Fullscreen Scroll-Experience: Ein Clothing-Design (Justins echte Skizze,
 * vektorisiert als SVG-Pfade) zeichnet sich beim Scrollen selbst — parallel
 * entsteht rechts ein Online-Shop, in dem das fertige Produkt landet.
 * Story: IDEE → SKIZZE → DESIGN → PRODUKT → SHOP → READY TO SELL
 *
 * Performance: Scroll steuert MotionValue, Updates laufen direkt ins DOM
 * (stroke-dashoffset / opacity / transform) — keine React-Rerenders pro Frame.
 */

const PATHS_URL = "/designs/outfit_b.paths.json";
const COLOR_IMG = "/designs/outfit_b.webp";

// Zeichen-Timeline (Anteile des Gesamt-Scrollfortschritts)
const T = {
  guidesIn: [0.0, 0.05],      // Konstruktionslinien erscheinen
  mainDraw: [0.06, 0.30],     // Silhouette / Hauptkonturen
  detailDraw: [0.26, 0.52],   // Details: Taschen, Nähte, Nieten
  guidesOut: [0.48, 0.56],    // Hilfslinien verblassen
  colorIn: [0.54, 0.64],      // Farbe/Flächen (Original ebendiert ein)
  flyToShop: [0.64, 0.80],    // Produkt fliegt in den Shop
  shopFade: [0.78, 0.86],     // große Skizze tritt zurück
  finale: [0.90, 1.0],        // READY TO SELL
};

// Shop-Aufbau-Stufen (rechts)
const SHOP_STEPS = [
  { key: "frame", s: 0.08, e: 0.16 },     // Browser-Rahmen + URL-Zeile
  { key: "nav", s: 0.14, e: 0.22 },       // Navigation
  { key: "hero", s: 0.20, e: 0.30 },      // Headlines
  { key: "blocks", s: 0.28, e: 0.40 },    // Content-Bereiche
  { key: "product", s: 0.42, e: 0.54 },   // Produktbereich
  { key: "pinfo", s: 0.68, e: 0.78 },     // Name + Preis
  { key: "sizes", s: 0.76, e: 0.84 },     // Größen
  { key: "cart", s: 0.84, e: 0.92 },      // Warenkorb-Button
];

const CHAPTERS = [
  { until: 0.06, kicker: "Deine Idee. Unsere Umsetzung. Dein Ergebnis.", title: "Die Idee." },
  { until: 0.28, kicker: "Schritt 01 — Skizze", title: "Die ersten Linien." },
  { until: 0.52, kicker: "Schritt 02 — Design", title: "Das Design entsteht." },
  { until: 0.64, kicker: "Schritt 03 — Produkt", title: "Das Produkt wird echt." },
  { until: 0.90, kicker: "Schritt 04 — Online-Shop", title: "Der Shop entsteht." },
  { until: 1.01, kicker: "Fertig", title: "Ready to sell." },
];

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const seg = (p, s, e) => clamp01((p - s) / (e - s));

// ---------------------------------------------------------------------------
// PHASE 0 — Cinematic WebGL-Intro (CinematicCanvas): 3D-Logo, Fracture,
// Partikel-Durchflug. Danach läuft die bestehende Story auf dem remappten q.
// ---------------------------------------------------------------------------
const INTRO_END = 0.3; // Anteil des Gesamt-Scrolls für die WebGL-Intro-Szene

export default function HeroExperience() {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const stageRef = useRef(null);
  const [data, setData] = useState(null);
  const [chapter, setChapter] = useState(0);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  // Pfade laden
  useEffect(() => {
    let alive = true;
    fetch(PATHS_URL)
      .then((r) => r.json())
      .then((d) => alive && setData(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Alle scroll-gesteuerten Updates — direkt ins DOM, kein Re-Render
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const stage = stageRef.current;
    if (!stage) return;

    // 0) Scroll-Hinweis: verschwindet sofort beim ersten Scrollen, kommt ganz oben wieder
    const hint = stage.querySelector("[data-scroll-hint]");
    if (hint) {
      const visible = p <= 0.003;
      hint.style.opacity = visible ? "1" : "0";
      hint.style.transform = visible ? "translate(-50%, 0)" : "translate(-50%, 12px)";
    }

    // 0b) INTRO (WebGL): CinematicCanvas rendert die 3D-Szene komplett scroll-
    // gesteuert selbst — hier nur der DOM-Handoff (Canvas löst sich am Ende auf,
    // die 2D-Szene dahinter war die ganze Zeit schon da).
    const logoScene = stage.querySelector("[data-logo-scene]");
    if (logoScene) {
      // visibility statt display: der Canvas behält seine Größe und wacht
      // beim Rückwärtsscrollen sofort wieder auf (display:none zerstörte ihn)
      if (reduced || p >= INTRO_END + 0.02) {
        logoScene.style.visibility = "hidden";
      } else {
        logoScene.style.visibility = "";
        logoScene.style.opacity = String(1 - seg(p, INTRO_END - 0.035, INTRO_END));
      }
    }

    // Die Website existierte „hinter" der 3D-Welt — Licht geht im Durchflug an
    const sceneWrap = stage.querySelector("[data-scene-wrap]");
    if (sceneWrap) sceneWrap.style.opacity = reduced ? "1" : String(0.12 + 0.88 * seg(p, 0.2, INTRO_END));
    const chWrap = stage.querySelector("[data-chapter-wrap]");
    if (chWrap) chWrap.style.opacity = reduced ? "1" : String(seg(p, 0.24, INTRO_END));

    // Bestehende Story läuft auf dem remappten Fortschritt (nach der Intro)
    const q = reduced ? p : seg(p, INTRO_END, 1);

    // 1) SVG-Pfade zeichnen (stroke-dashoffset via pathLength=1-Trick)
    if (svgRef.current) {
      svgRef.current.querySelectorAll("path[data-s]").forEach((el) => {
        const s = parseFloat(el.dataset.s);
        const e = parseFloat(el.dataset.e);
        el.style.strokeDashoffset = String(1 - seg(q, s, e));
      });
      // Hilfslinien: rein 0-5 %, raus 48-56 %
      const gOp = seg(q, T.guidesIn[0], T.guidesIn[1]) * (1 - seg(q, T.guidesOut[0], T.guidesOut[1]));
      const guides = svgRef.current.querySelector("[data-guides]");
      if (guides) guides.style.opacity = String(gOp * 0.5);
      // Farb-Layer (Originalfoto blendet über die Linien)
      const color = stage.querySelector("[data-color-layer]");
      if (color) color.style.opacity = String(seg(q, T.colorIn[0], T.colorIn[1]));
      // Zeichen-SVG tritt ab 0.78 leicht zurück, wenn das Produkt im Shop ist
      const sketch = stage.querySelector("[data-sketch]");
      if (sketch) {
        const back = seg(q, T.shopFade[0], T.shopFade[1]);
        sketch.style.opacity = String(1 - back * 0.15);
        sketch.style.transform = `scale(${1 - back * 0.04})`;
      }
    }

    // 2) Fliegendes Produkt: von der Skizze in die Shop-Karte
    const flyer = stage.querySelector("[data-flyer]");
    if (flyer) {
      const f = seg(q, T.flyToShop[0], T.flyToShop[1]);
      if (f <= 0 || f >= 1) {
        flyer.style.opacity = "0";
      } else {
        const ease = f * f * (3 - 2 * f); // smoothstep
        flyer.style.opacity = String(Math.min(1, f * 4) * (1 - Math.max(0, f - 0.85) / 0.15));
        flyer.style.transform = `translate(${ease * 34}vw, ${ease * 6}vh) scale(${1 - ease * 0.62})`;
      }
    }

    // 3) Shop-Elemente gestaffelt aufbauen
    stage.querySelectorAll("[data-shop-step]").forEach((el) => {
      const step = SHOP_STEPS.find((x) => x.key === el.dataset.shopStep);
      if (!step) return;
      const v = seg(q, step.s, step.e);
      el.style.opacity = String(v);
      el.style.transform = `translateY(${(1 - v) * 14}px)`;
    });
    const shopImg = stage.querySelector("[data-shop-product-img]");
    if (shopImg) {
      const v = seg(q, T.flyToShop[1] - 0.06, T.flyToShop[1] + 0.02);
      shopImg.style.opacity = String(v);
      shopImg.style.transform = `scale(${0.92 + v * 0.08})`;
    }

    // 4) Finale: READY TO SELL Badge
    const finale = stage.querySelector("[data-finale]");
    if (finale) {
      const v = seg(q, T.finale[0], T.finale[1]);
      finale.style.opacity = String(v);
      finale.style.transform = `translate(-50%, 0) scale(${0.9 + v * 0.1})`;
    }

    // 5) Fortschrittsbalken
    const bar = stage.querySelector("[data-progress]");
    if (bar) bar.style.transform = `scaleY(${p})`;

    // 6) Kapitel-Text (einziger React-State — ändert sich selten)
    const idx = CHAPTERS.findIndex((c) => q < c.until);
    const next = idx === -1 ? CHAPTERS.length - 1 : idx;
    setChapter((cur) => (cur === next ? cur : next));
  });

  const ch = CHAPTERS[chapter];

  return (
    <section ref={wrapRef} className="relative h-[820vh] bg-[#0a0a0a]" style={{ position: "relative" }} data-testid="hero-experience">
      <div ref={stageRef} className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Grid-Hintergrund wie ein Skizzenblock */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(212,175,55,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.07) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        {/* Kapitel-Text oben links (fadet am Ende der Logo-Intro ein) */}
        <div className="absolute top-20 md:top-24 left-6 md:left-12 z-20 max-w-[70vw]" data-testid="hero-xp-chapter" data-chapter-wrap style={{ opacity: reduced ? 1 : 0 }}>
          <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2 md:mb-3">
            {ch.kicker}
          </p>
          <h1 className="font-display uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-paper">
            {ch.title}
          </h1>
        </div>

        {/* Fortschrittslinie rechts */}
        <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 h-40 w-px bg-white/10 z-20">
          <div data-progress className="w-full h-full bg-gold origin-top" style={{ transform: "scaleY(0)" }} />
        </div>

        {/* Layout: Skizze links, Shop rechts — mobil untereinander (existiert „hinter" dem Logo) */}
        <div className="relative z-10 h-full w-full flex flex-col md:flex-row items-center justify-center gap-2 md:gap-8 px-4 md:px-16 pt-28 md:pt-20 pb-8" data-scene-wrap style={{ opacity: reduced ? 1 : 0.12 }}>
          {/* SKIZZE */}
          <div className="relative w-full md:w-[46%] h-[52vh] md:h-[82vh] flex items-center justify-center" data-sketch>
            {data && (
              <svg
                ref={svgRef}
                viewBox={data.viewBox}
                className="h-full w-auto max-w-full"
                data-testid="hero-xp-sketch"
                fill="none"
              >
                {/* Konstruktions-Hilfslinien */}
                <g data-guides opacity="0" stroke="#d4af37" strokeWidth="1.5" strokeDasharray="6 10">
                  {(() => {
                    const [, , w, h] = data.viewBox.split(" ").map(Number);
                    return (
                      <>
                        <line x1={w / 2} y1={h * 0.02} x2={w / 2} y2={h * 0.98} />
                        <line x1={w * 0.1} y1={h * 0.16} x2={w * 0.9} y2={h * 0.16} />
                        <line x1={w * 0.1} y1={h * 0.46} x2={w * 0.9} y2={h * 0.46} />
                        <line x1={w * 0.1} y1={h * 0.62} x2={w * 0.9} y2={h * 0.62} />
                      </>
                    );
                  })()}
                </g>
                {/* Hauptkonturen (zeichnen sich zuerst) */}
                {data.main.map((p, i) => {
                  const n = data.main.length;
                  const span = T.mainDraw[1] - T.mainDraw[0];
                  const s = T.mainDraw[0] + (i / n) * span * 0.75;
                  return (
                    <path
                      key={`m${i}`}
                      d={p.d}
                      stroke="#fafaf8"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      pathLength={1}
                      strokeDasharray={1}
                      strokeDashoffset={1}
                      data-s={s.toFixed(4)}
                      data-e={Math.min(T.mainDraw[1], s + span * 0.25).toFixed(4)}
                    />
                  );
                })}
                {/* Details (zeichnen sich danach, dünner) */}
                {data.details.map((p, i) => {
                  const n = data.details.length;
                  const span = T.detailDraw[1] - T.detailDraw[0];
                  const s = T.detailDraw[0] + (i / n) * span * 0.8;
                  return (
                    <path
                      key={`d${i}`}
                      d={p.d}
                      stroke="#e8e4d8"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      pathLength={1}
                      strokeDasharray={1}
                      strokeDashoffset={1}
                      data-s={s.toFixed(4)}
                      data-e={Math.min(T.detailDraw[1], s + span * 0.2).toFixed(4)}
                    />
                  );
                })}
              </svg>
            )}
            {/* Farb-Layer: Originalfoto blendet ein (mix-blend: dunkles Grid verschmilzt) */}
            <img
              src={COLOR_IMG}
              alt="Fertiges Clothing Design — Hoodie und Cargo-Hose"
              data-color-layer
              className="absolute h-[52vh] md:h-[82vh] w-auto max-w-full object-contain pointer-events-none mix-blend-screen"
              style={{ opacity: 0 }}
            />
          </div>

          {/* Fliegendes Produkt (Skizze -> Shop) */}
          <img
            src={COLOR_IMG}
            alt=""
            aria-hidden
            data-flyer
            className="absolute left-[8%] md:left-[16%] top-1/2 -translate-y-1/2 h-[30vh] md:h-[44vh] w-auto z-30 pointer-events-none"
            style={{ opacity: 0 }}
          />

          {/* SHOP-MOCKUP */}
          <div className="relative w-full md:w-[42%] h-[32vh] md:h-[76vh] flex items-center justify-center">
            <div
              data-shop-step="frame"
              className="w-full max-w-[520px] h-full bg-[#fafaf8] rounded-xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.55)] flex flex-col"
              style={{ opacity: 0 }}
              data-testid="hero-xp-shop"
            >
              {/* Browser-Chrome */}
              <div className="flex items-center gap-2 px-3 py-2 bg-[#eceae4] border-b border-black/10 shrink-0">
                <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                <span className="ml-2 flex-1 bg-white rounded-full text-[9px] text-zinc-500 px-3 py-0.5 truncate">
                  deine-brand.de/shop
                </span>
              </div>
              {/* Shop-Inhalt */}
              <div className="flex-1 p-3 md:p-4 flex flex-col gap-2 md:gap-3 min-h-0">
                <div data-shop-step="nav" className="flex items-center justify-between shrink-0" style={{ opacity: 0 }}>
                  <span className="font-display font-bold uppercase text-[11px] md:text-sm text-ink">Deine Brand</span>
                  <div className="flex gap-2 md:gap-3">
                    <span className="w-6 md:w-8 h-1.5 bg-ink/15 rounded-full" />
                    <span className="w-6 md:w-8 h-1.5 bg-ink/15 rounded-full" />
                    <span className="w-6 md:w-8 h-1.5 bg-gold rounded-full" />
                  </div>
                </div>
                <div data-shop-step="hero" className="shrink-0 space-y-1.5" style={{ opacity: 0 }}>
                  <div className="w-3/4 h-3 md:h-4 bg-ink rounded-sm" />
                  <div className="w-1/2 h-2 md:h-2.5 bg-ink/25 rounded-sm" />
                </div>
                <div data-shop-step="blocks" className="grid grid-cols-3 gap-1.5 md:gap-2 shrink-0" style={{ opacity: 0 }}>
                  <div className="h-5 md:h-8 bg-ink/8 border border-black/10 rounded" />
                  <div className="h-5 md:h-8 bg-ink/8 border border-black/10 rounded" />
                  <div className="h-5 md:h-8 bg-ink/8 border border-black/10 rounded" />
                </div>
                {/* Produktkarte */}
                <div data-shop-step="product" className="flex-1 min-h-0 border border-black/10 rounded-lg bg-white p-2 md:p-3 flex gap-2 md:gap-3" style={{ opacity: 0 }}>
                  <div className="w-[42%] bg-[#111] rounded-md overflow-hidden flex items-center justify-center">
                    <img
                      src={COLOR_IMG}
                      alt="Produkt: Hoodie und Cargo-Hose"
                      data-shop-product-img
                      className="w-full h-full object-contain"
                      style={{ opacity: 0 }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1.5 md:gap-2 min-w-0">
                    <div data-shop-step="pinfo" style={{ opacity: 0 }}>
                      <p className="font-display uppercase text-[10px] md:text-xs text-ink leading-tight">Signature Hoodie Set</p>
                      <p className="text-[10px] md:text-xs text-gold font-bold mt-0.5">129,00 €</p>
                    </div>
                    <div data-shop-step="sizes" className="flex gap-1" style={{ opacity: 0 }}>
                      {["S", "M", "L", "XL"].map((s, i) => (
                        <span
                          key={s}
                          className={`w-5 md:w-6 h-5 md:h-6 text-[8px] md:text-[9px] flex items-center justify-center border rounded-sm ${
                            i === 1 ? "border-gold bg-gold/10 text-ink font-bold" : "border-black/15 text-zinc-500"
                          }`}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    <div data-shop-step="cart" className="mt-auto" style={{ opacity: 0 }}>
                      <div className="bg-ink text-paper text-[8px] md:text-[10px] uppercase tracking-[0.15em] text-center py-1.5 md:py-2 rounded-sm">
                        In den Warenkorb
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PHASE 0 — Cinematic WebGL-Intro: JUSTIN-Logo als echtes 3D-Objekt.
            Die Kamera (Scroll) fliegt durch das physisch zerbrechende Logo hindurch
            in die Website. Die Szene rendert sich selbst (demand-Frameloop). */}
        {!reduced && (
          <div
            data-logo-scene
            data-testid="hero-xp-logo-scene"
            className="absolute inset-0 z-30 pointer-events-none overflow-hidden"
            style={{ background: "#050505" }}
          >
            <CinematicCanvas progress={scrollYProgress} introEnd={INTRO_END} />
          </div>
        )}

        {/* Finale-Badge */}
        <div
          data-finale
          className="absolute left-1/2 bottom-24 md:bottom-16 z-30 pointer-events-none"
          style={{ opacity: 0, transform: "translate(-50%, 0) scale(0.9)" }}
        >
          <span className="inline-block border border-gold bg-ink/80 backdrop-blur px-6 py-3 text-gold text-xs md:text-sm uppercase tracking-[0.35em] font-bold" data-testid="hero-xp-finale">
            Ready to sell
          </span>
        </div>

        {/* Scroll-Hinweis am Anfang — prominent, verschwindet beim ersten Scrollen */}
        <div
          data-scroll-hint
          data-testid="hero-xp-scroll-hint"
          className="absolute bottom-16 md:bottom-24 left-1/2 z-40 pointer-events-none transition-all duration-500"
          style={{ transform: "translate(-50%, 0)" }}
        >
          <span className="inline-flex flex-col items-center gap-3 border border-gold/70 bg-ink/70 backdrop-blur px-8 py-4">
            <span className="text-goldlight text-xs md:text-sm uppercase tracking-[0.3em] font-bold">
              Scrollen — die Story steuerst du
            </span>
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="text-gold"
            >
              <ChevronDown size={20} />
            </motion.span>
          </span>
        </div>
      </div>
    </section>
  );
}
